import React from "react";
import { RelayEnvironmentProvider } from "react-relay";

import { faClock, faUser } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faEllipsisV } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import moment from "moment";
import { Node as ProseNode } from "prosemirror-model";
import { EditorView } from "prosemirror-view";
import { el, setAttr } from "redom";
import * as uuid from "uuid";

import AppContext from "~/common/AppContext";
import { AvatarsDOMComponent } from "~/common/Assignees";
import type { AttendeeAttrs } from "~/common/Attendees";
import { DueDateDOMComponent } from "~/common/DueDate";
import { Viewport } from "~/common/ViewportContext";
import { cloneNode, type DOMComponent, getElementByClassName, render, setStyles } from "~/lib/dom";
import { getContext } from "~/lib/prose/plugins/context";
import { reactRender } from "~/lib/prose/plugins/reactHost";
import { isNodeAssignable } from "~/lib/prose/utils";
import { clearTooltip, setTooltip } from "~/lib/utils";
import { environment } from "~/models";
import AssignableDropdown, {
	type AssignableDropdownMenuSections,
	type AssigneeStatusType,
} from "~/notes/AssignableDropdown";
import { jumpToOriginalBlock } from "~/notes/useActionItemJump";
import { colors } from "~/ui";
import { IconButton } from "~/ui/button/IconButton";
import Avatar, { LazyAvatar } from "~/ui/legacy/Avatar";

import { getBlockActionsPlugin } from "./plugins/blockActions";

import stylesActions from "~/actionItems/ActionItemActions.module.css";

import assignablePluginStyles from "./css/assignable.module.css";
import styles from "./css/blockActions.module.css";

type Triggers = readonly HTMLElement[];

export function getUser(): React.ContextType<typeof AppContext>["user"] {
	// @ts-ignore
	return AppContext._currentValue.user; // eslint-disable-line no-underscore-dangle
}

export interface BlockActionMenuData {
	view: EditorView;
	menuSections: React.ComponentProps<typeof AssignableDropdown>["menuSections"];
	triggers: Triggers;
	node: ProseNode;
}

function sameTriggers(a: Triggers, b: Triggers): boolean {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i += 1) {
		if (a[i] !== b[i]) return false;
	}
	return true;
}

function sameNodeType(a: ProseNode, b: ProseNode): boolean {
	return a.type.name === b.type.name;
}

export class BlockActionMenu implements DOMComponent<BlockActionMenuData> {
	el: HTMLElement;
	private data?: BlockActionMenuData;
	private kebab: HTMLElement;
	private dropdownOpenedFrom?: HTMLElement;
	private menuRoot?: HTMLElement;

	static template = el(
		"div",
		{ class: styles.blockActionsContainer, contenteditable: "false", "data-block-actions-container": "" },
		el(
			"div",
			{ class: cx("triggers", styles.triggers) },
			render(
				<IconButton
					size="small"
					variant="tertiary"
					className={cx(styles.actionsKebab, stylesActions.actionsKebab)}
					icon={faEllipsisV}
					contentEditable={false}
					type="button"
				/>,
			),
		),
	);

	// template for editing an assignable node in custom section template
	static customSectionTemplate = el(
		"div",
		{ class: styles.blockActionsContainer, contenteditable: "false", "data-block-actions-container": "" },
		el(
			"div",
			{ class: cx("triggers", styles.triggers) },
			render(
				<IconButton
					size="small"
					variant="tertiary"
					className={cx(styles.actionsKebab, stylesActions.actionsKebab, styles.customSectionTips)}
					icon={faUser}
					contentEditable={false}
					type="button"
				/>,
			),
		),
	);

	constructor(data: BlockActionMenuData) {
		const context = getContext(data.view.state);
		if (context?.isCustomSection) {
			this.el = cloneNode(BlockActionMenu.customSectionTemplate);
		} else {
			this.el = cloneNode(BlockActionMenu.template);
		}
		const kebab = getElementByClassName(this.el, cx(styles.actionsKebab, stylesActions.actionsKebab));
		if (!(kebab instanceof HTMLElement)) throw new Error("Invalid template");
		this.kebab = kebab;
		kebab.onclick = this.onDropdownOpen;
		this.getMenuSections = this.getMenuSections.bind(this);
		this.update(data);
	}

	update(data: BlockActionMenuData) {
		if (!this.data || !sameTriggers(this.data.triggers, data.triggers)) {
			if (this.data) this.unlisten(this.data.triggers);
			while (this.kebab.previousSibling) {
				this.kebab.previousSibling.remove();
			}
			const triggersContainer = getElementByClassName(this.el, styles.triggers);
			if (triggersContainer) {
				data.triggers.forEach(t => {
					triggersContainer.insertBefore(t, this.kebab);
					t.addEventListener("click", this.onDropdownOpen);
				});
			}
		}

		// extra class name for assignable items
		if (!this.data || !sameNodeType(this.data.node, data.node)) {
			// remove the possible existing alignment class if the node type changed
			if (this.data) {
				const prevAlignmentClass = isNodeAssignable(this.data.node)
					? styles.blockActionsContainer__assignable
					: this.data.node.type.name === "header-one"
						? styles.blockActionsContainer__headerOne
						: undefined;
				if (prevAlignmentClass) {
					this.el.classList.remove(prevAlignmentClass);
				}
			}
			// apply the possible alignment class if the new node type match one of the below types
			const isAssignable = isNodeAssignable(data.node);
			const isHeaderOne = data.node.type.name === "header-one";
			// these two types of node use different alignment methods
			const alignmentClass = isAssignable
				? styles.blockActionsContainer__assignable
				: isHeaderOne
					? styles.blockActionsContainer__headerOne
					: undefined;
			if (alignmentClass) {
				this.el.classList.add(alignmentClass);
			}
		}

		this.data = data;
	}

	deinit() {
		if (this.data) {
			this.unlisten(this.data.triggers);
			if (this.menuRoot) {
				reactRender(this.data.view, this.menuRoot, null, true);
			}
		}
	}

	private unlisten(triggers: Triggers): void {
		triggers.forEach(t => t.removeEventListener("click", this.onDropdownOpen));
	}

	private getMenuSections(): (close: () => void) => AssignableDropdownMenuSections {
		const { data } = this;
		if (!data) return () => [];
		const { view } = data;
		const blockActionsPlugin = getBlockActionsPlugin(view);
		const postProcess = blockActionsPlugin?.options.postProsessMenuSections;
		if (postProcess) {
			return (c: () => void) => postProcess(c, data.menuSections);
		}
		return data.menuSections;
	}

	private onDropdownOpen = (e: MouseEvent) => {
		const { currentTarget } = e;
		const { data } = this;
		if (!data || !(currentTarget instanceof HTMLElement)) return;

		// User clicked the same trigger again, we'll allow the dropdown to close
		// without opening it again.
		if (currentTarget === this.dropdownOpenedFrom) return;

		this.dropdownOpenedFrom = currentTarget;

		const context = getContext(data.view.state);
		// inside custom section modal, scroll bar could present which makes the dropdown menu
		// always render downward and got cut off by the scrollable container.
		// in order to escape from that container we position the menu relative to the wrapper of the
		// prosereact ref, which allows the menu visually jumps out the editor but still attach to the dropdown trigger
		const relativeToPmWrapper = context?.isCustomSection;

		const root = document.createElement("div");
		root.setAttribute("portal-id", uuid.v4());
		document.body.appendChild(root);
		const dest = document.createElement("div");
		const blockActionsPlugin = getBlockActionsPlugin(data.view);
		const forceDesktopDropdown = blockActionsPlugin?.options?.forceDesktopDropdown ?? false;
		dest.setAttribute("data-force-desktop-dropdown", forceDesktopDropdown ? "true" : "false");
		if (forceDesktopDropdown || Viewport.isTablet()) {
			if (relativeToPmWrapper) {
				// append the menu to the wrapper of the prosereact ref
				data?.view.dom.parentNode?.appendChild(dest);
			} else {
				this.el.appendChild(dest);
			}
		} else {
			document.body.appendChild(dest);
		}
		const subView = currentTarget.dataset.initialSubView;

		const el = (
			<AssignableDropdown
				initialSubView={subView}
				portal={dest}
				// eslint-disable-next-line fellow/no-inline-disable
				// eslint-disable-next-line react/jsx-no-bind
				onClose={() => {
					reactRender(data.view, root, null, true);
					this.menuRoot = undefined;

					// Only remove the currently active trigger if it's still the same
					// one that opened this dropdown.
					if (this.dropdownOpenedFrom === currentTarget) {
						delete this.dropdownOpenedFrom;
					}

					// Delay this by 100ms since flushing the react host changes could be
					// debounced by up to 100ms.
					setTimeout(() => {
						dest.remove();
						root.remove();
					}, 100);
				}}
				menuSections={this.getMenuSections()}
				anchor={currentTarget}
				editorView={data.view}
				relativeToPmWrapper={relativeToPmWrapper}
				doNotOverflowEditor={!context?.isBlockTextEditor}
				forceDesktop={forceDesktopDropdown}
			/>
		);

		reactRender(data.view, root, el, true);
		this.menuRoot = root;
	};
}

export interface DueDateTriggerData {
	node: ProseNode;
	view: EditorView;
}

const clockNode = render(
	<IconButton
		icon={faClock}
		variant="tertiary"
		className={cx("assignable-tools-duedate", assignablePluginStyles.actionsClock, stylesActions.actionsClock)}
		contentEditable={false}
	/>,
);

if (!(clockNode instanceof HTMLButtonElement)) throw new Error();

clockNode.dataset.initialSubView = "dueDate";
const clockElement: HTMLElement = clockNode;

const dueDateClasses = cx("time", assignablePluginStyles.dueDate);

function linkedItemTooltip(hasAccessToOriginalNote: boolean) {
	return setTooltip(
		hasAccessToOriginalNote
			? "Go to and edit the original action item"
			: "You don’t have access to the original action item",
		"bottom right",
		true,
		true,
		hasAccessToOriginalNote ? "11.667em" : "12.5em",
	);
}

// Returns a boolean indicating whether the element was disabled (i.e. should
// not be re-enabled).
function applyLinkedAttributes(el: HTMLButtonElement, view: EditorView, node: ProseNode): boolean {
	const context = getContext(view.state);

	const { originalBlockId } = node.attrs;
	const originatingNoteId = context?.noteId;
	const hasAccessToOriginalNote = !!context?.hasAccessToOriginalBlock?.[originalBlockId];

	el.disabled = !hasAccessToOriginalNote;
	const tooltipProps = linkedItemTooltip(hasAccessToOriginalNote);
	setAttr(el, tooltipProps);
	if (tooltipProps.style) setStyles(el, tooltipProps.style);

	el.onclick = e => {
		e.preventDefault();
		jumpToOriginalBlock(originalBlockId, originatingNoteId, context?.isTeams || false);
	};

	return el.disabled;
}

function clearLinkedAttributes(el: HTMLButtonElement): void {
	el.disabled = false;
	clearTooltip(el);
	el.onclick = null;
}

export class DueDateTrigger implements DOMComponent<DueDateTriggerData> {
	el: HTMLButtonElement;
	private data?: DueDateTriggerData;
	private dueDate?: string;
	private dueDateComponent?: DueDateDOMComponent;

	static template = el("button", {
		type: "button",
		class: assignablePluginStyles.dueDateButton,
		contenteditable: "false",
		dataset: {
			initialSubView: "dueDate",
		},
	});

	constructor(data: DueDateTriggerData) {
		// onclick is added by BlockActionsMenu
		this.el = cloneNode(DueDateTrigger.template);
		this.update(data);
	}

	deinit() {
		this.dueDateComponent?.deinit();
	}

	update(data: DueDateTriggerData) {
		const { node, view } = data;
		const { done, wontDo, dueDate } = node.attrs;
		const doneOrWontDo = done || wontDo;

		// Do this part first since it might replace this.el
		if (
			!this.data ||
			dueDate !== this.dueDate ||
			doneOrWontDo !== (this.data.node.attrs.done || this.data.node.attrs.wontDo)
		) {
			if (dueDate) {
				this.showDueDate(dueDate, doneOrWontDo);
			} else if (view.editable) {
				this.showClock();
			}

			this.dueDate = dueDate;
		}

		let keepDisabled = false;
		if (node.attrs.originalBlockId) {
			if (node.attrs.originalBlockId !== !this.data?.node.attrs.originalBlockId) {
				if (this.data?.node.attrs.originalBlockId) clearLinkedAttributes(this.el);
				keepDisabled = applyLinkedAttributes(this.el, view, node);
			}
		} else if (this.data?.node.attrs.originalBlockId) {
			clearLinkedAttributes(this.el);
		}

		if (!keepDisabled && this.el.disabled && view.editable) {
			this.el.disabled = false;
		} else if (!this.el.disabled && !view.editable) {
			this.el.disabled = true;
		}

		this.el.classList.toggle("done", doneOrWontDo);

		this.data = data;
	}

	private showDueDate(dueDate: string, done: boolean) {
		if (!this.dueDate) {
			const newEl = cloneNode(DueDateTrigger.template);
			this.el.replaceWith(newEl);
			this.el = newEl;
		}

		const date = moment(dueDate, "YYYY-MM-DD");

		this.el.dataset.dueDate = dueDate;

		if (this.dueDateComponent) {
			this.dueDateComponent.update({ date, done, class: dueDateClasses });
		} else {
			this.dueDateComponent = new DueDateDOMComponent({ date, done, class: dueDateClasses });
			this.el.append(this.dueDateComponent.el);
		}
	}

	private showClock() {
		const clock = cloneNode(clockElement);
		if (!(clock instanceof HTMLButtonElement)) throw new Error("invalid template");
		// If `this.el` has a parent, it means this component is already mounted to
		// the DOM. In that case we should replace the mounted DOM node with the
		// new one in addition to updating `this.el`. If it's not already mounted,
		// we just have to update the attribute.
		if (this.el.parentNode) {
			this.el.replaceWith(clock);
		}
		this.el = clock;
		this.dueDateComponent?.deinit();
		this.dueDateComponent?.el.remove();
		delete this.dueDateComponent;
	}
}

export interface AssigneeTriggerData {
	node: ProseNode;
	view: EditorView;
	viewerId?: string;
	isPublic?: boolean;
}

export class AssigneeTrigger implements DOMComponent<AssigneeTriggerData> {
	el: HTMLButtonElement;
	private data?: AssigneeTriggerData;
	private reactRendered = false;

	static template = el("button", {
		type: "button",
		class: cx(assignablePluginStyles.actionsAssignee, stylesActions.actionsAssignee),
		contenteditable: "false",
		dataset: { initialSubView: "reassign" },
	});

	static unassignedTemplate = el(
		"div",
		{ class: "assignable-unassigned", dataset: { initialSubView: "reassign" } },
		render(<FontAwesomeIcon icon={faUser} />),
	);

	constructor(data: AssigneeTriggerData) {
		this.el = cloneNode(AssigneeTrigger.template);
		this.el.setAttribute("portal-id", uuid.v4());
		if (!data.isPublic) this.update(data);
	}

	deinit() {
		if (this.reactRendered && this.data?.view) {
			reactRender(this.data.view, this.el, null, true);
		}
	}

	update(data: AssigneeTriggerData) {
		const { node, view, viewerId } = data;
		const { assignees } = node.attrs;
		const context = getContext(view.state);
		// We want to compare if both assignees lists are the same
		// There a few ways to do this but here we will sort both arrays
		// And transform them to string, them we can compare that string
		const nodeAttrsAssigneeIds = assignees ? [...assignees].map((assignee: AssigneeStatusType) => assignee.id) : [];
		const sortedAssignees = [...nodeAttrsAssigneeIds];
		sortedAssignees.sort();
		const sortedDataAssignees = this.data?.node.attrs.assignees
			? [...this.data.node.attrs.assignees].map((assignee: AssigneeStatusType) => assignee.id)
			: [];
		sortedDataAssignees.sort();
		const assigneesString = sortedAssignees.toString();
		const dataAssigneesString = sortedDataAssignees.toString();
		if (!this.data || assigneesString !== dataAssigneesString) {
			// FIXME: Update existing avatar instead of recreating?
			if (this.reactRendered) {
				reactRender(view, this.el, null, true);
				this.reactRendered = false;
			} else {
				while (this.el.firstChild) this.el.removeChild(this.el.firstChild);
			}
			const assigneeUsers = AssigneeTrigger.getAssigneeUsers(node, view);
			// TODO: Can we make this even better and do it a mix of both when one of the assignees doesn't
			// Come from the getAssigneeUsers function?
			if (
				context.isPublicTemplateView ||
				(assigneeUsers && assigneeUsers.length > 0 && assigneeUsers.length === node.attrs.assignees.length)
			) {
				this.el.appendChild(
					new AvatarsDOMComponent({
						users: assigneeUsers,
						avatarWidth: "1.5em",
						isDarkMode: context.isDarkMode,
						viewerId,
					}).el,
				);
			} else if (nodeAttrsAssigneeIds && nodeAttrsAssigneeIds.length > 0) {
				const assigneeUsersMap = new Map<string, AttendeeAttrs>();
				assigneeUsers?.forEach(assignee => assigneeUsersMap.set(assignee.id, assignee));
				// We want to render the viewer first, so let's sort by that
				let usersToRender = [...nodeAttrsAssigneeIds];
				usersToRender.sort((a, _b) => (a === viewerId ? -1 : 0));
				// We want to render 3 assignees max on desktop
				// The list uses row-reverse, so to keep our order let's reverse the list
				// TODO: Change based on the screen's width
				usersToRender = usersToRender.slice(0, nodeAttrsAssigneeIds.length <= 3 ? 3 : 2).filter(user => user);
				usersToRender.reverse();
				reactRender(
					view,
					this.el,
					<RelayEnvironmentProvider environment={environment}>
						<div className="assignee-avatars">
							{nodeAttrsAssigneeIds.length > usersToRender.length && (
								<span>
									<Avatar
										user={{
											id: "",
										}}
										width="1.5em"
										initials={nodeAttrsAssigneeIds.length - usersToRender.length}
										uiAvatarOptions={{
											background: colors.grey[200].substring(1),
											color: colors.grey[600].substring(1),
										}}
									/>
								</span>
							)}
							{usersToRender.map((assignee: string) => (
								<span key={assignee}>
									<LazyAvatar
										userId={assignee}
										preloader={<></>}
										dashedOutline={
											assigneeUsersMap.has(assignee) ? assigneeUsersMap.get(assignee)?.isExternalInNote : true
										}
										link={false}
										style={{ fontSize: "0.75em" }}
									/>
								</span>
							))}
						</div>
					</RelayEnvironmentProvider>,
					true,
				);
				this.reactRendered = true;
			} else {
				this.el.appendChild(cloneNode(AssigneeTrigger.unassignedTemplate));
			}
		}

		let keepDisabled = false;
		if (node.attrs.originalBlockId) {
			if (node.attrs.originalBlockId !== !this.data?.node.attrs.originalBlockId) {
				if (this.data?.node.attrs.originalBlockId) clearLinkedAttributes(this.el);
				keepDisabled = applyLinkedAttributes(this.el, view, node);
			}
		} else if (this.data?.node.attrs.originalBlockId) {
			clearLinkedAttributes(this.el);
		}

		if (!keepDisabled && this.el.disabled && view.editable) {
			this.el.disabled = false;
		} else if (!this.el.disabled && !view.editable) {
			this.el.disabled = true;
		}

		this.el.classList.toggle("read-only", !data.view.editable);

		this.data = data;
	}

	static getAssigneeUsers(node: ProseNode, view: EditorView): Array<AttendeeAttrs> {
		const context = getContext(view.state);
		const assigneesSet = new Set(node.attrs.assignees.map((assignee: { [key: string]: string }) => assignee.id) || []);
		return (
			node.attrs.assignees &&
			context?.participants?.filter((a: AttendeeAttrs) => {
				const userId = a.id || a.user?.id;
				if (!userId) return false;
				return assigneesSet.has(userId);
			})
		);
	}

	static shouldShow(node: ProseNode, view: EditorView): boolean {
		const context = getContext(view.state);

		const originatingNoteId = context?.noteId,
			hasOtherParticipants =
				(!!context?.participants && context?.participants.length > 1) ||
				(node.attrs.assignees &&
					!node.attrs.assignees.some((assignee: { [key: string]: string }) => context.userId === assignee.id));

		return originatingNoteId != null && hasOtherParticipants && (view.editable || node.attrs.assignees.length > 0);
	}
}
